import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { AppointmentSnapshot } from '#services/api/insights.ts';
import type { loader } from './AppointmentsDrilldown.data.ts';

import * as React from 'react';
import { Dialog } from '@mend/mui';
import { Chip, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { endOfDay, format, startOfDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLoaderData, useSearchParams } from 'react-router';

import { FiltersDisclaimerDialog } from '#components/Drilldown/FiltersDisclaimerDialog.tsx';
import { NoWrapText } from '#components/Drilldown/NoWrapText.tsx';
import { Toolbar } from '#components/Drilldown/Toolbar.tsx';
import { SEARCH_MIN_CHARS } from '#components/Drilldown/Toolbar.utils.ts';
import { useClose } from '#components/Drilldown/use-close.ts';
import { useColumnVisibility } from '#components/Drilldown/use-column-visibility.ts';
import { appointmentSnapshot } from '#lib/react-query/queries.ts';
import { convertUtcStringToLocalDate, formatDurationFromSeconds, FORMATS } from '#utils/dates.ts';
import { formatPhoneNumber } from '#utils/phone.ts';
import { hasPlaceholderEmail } from '#utils/user.ts';
import { AppointmentsFiltersPanel } from './AppointmentsFiltersPanel.tsx';
import { ValueWithValidity } from './ValueWithValidity.tsx';

const columns: GridColDef<AppointmentSnapshot>[] = [
  { field: 'id', headerName: 'Appointment ID', width: 130 },
  {
    field: 'patientFirstName',
    headerName: 'Patient First Name',
    valueFormatter: (value: AppointmentSnapshot['patientFirstName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 150,
  },
  {
    field: 'patientLastName',
    headerName: 'Patient Last Name',
    valueFormatter: (value: AppointmentSnapshot['patientLastName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 150,
  },
  {
    field: 'patientPreferredFirstName',
    headerName: 'Patient Preferred Name',
    valueFormatter: (value: AppointmentSnapshot['patientPreferredFirstName']) => {
      if (!value) return '--';
      return String(value);
    },
    width: 190,
  },
  {
    field: 'patientBirthDate',
    headerName: 'Patient DOB',
    valueFormatter: (value: AppointmentSnapshot['patientBirthDate']) => {
      const [year, month, date] = value.split('-') as [string, string, string];
      return `${month}/${date}/${year}`;
    },
    width: 110,
  },
  {
    field: 'patientChartId',
    headerName: 'Patient Chart ID',
    valueFormatter: (value: AppointmentSnapshot['patientChartId']) => value ?? '--',
    width: 130,
  },
  {
    field: 'patientEmail',
    headerName: 'Patient Email',
    renderCell: (
      params: GridRenderCellParams<AppointmentSnapshot, AppointmentSnapshot['patientEmail']>
    ) => {
      // The `undefined` in `params.value` comes from the generic type not
      // being able to determine the field we use (`patientEmail`) will always
      // return a non-`undefined` value, so it's perfectly safe to use the
      // non-null assertion operator here.
      const value = params.value!;
      return <ValueWithValidity value={value} isValid={!hasPlaceholderEmail(value)} />;
    },
    width: 110,
  },
  {
    field: 'patientPhone',
    headerName: 'Patient Phone',
    renderCell: (
      params: GridRenderCellParams<AppointmentSnapshot, AppointmentSnapshot['patientPhone']>
    ) => {
      if (!params.value) return '--';
      return (
        <ValueWithValidity
          value={formatPhoneNumber(params.value)}
          isValid={Boolean(params.row.patientHasValidPhone)}
        />
      );
    },
    width: 120,
  },
  { field: 'providerFirstName', headerName: 'Provider First Name', width: 160 },
  { field: 'providerLastName', headerName: 'Provider Last Name', width: 160 },
  { field: 'appointmentTypeType', headerName: 'Appointment Type', width: 150 },
  {
    field: 'appointmentDate',
    headerName: 'Appointment Date',
    valueGetter: (_params, row) => convertUtcStringToLocalDate(row.startDate),
    valueFormatter: (value: Date) => format(value, 'MM/dd/yyyy'),
    width: 150,
  },
  {
    field: 'appointmentTime',
    headerName: 'Appointment Time',
    valueGetter: (_params, row) => convertUtcStringToLocalDate(row.startDate),
    valueFormatter: (value: Date) => format(value, 'h:mmaaa'),
    width: 150,
  },
  {
    field: 'noShow',
    headerName: 'No-show',
    valueFormatter: (value: AppointmentSnapshot['noShow']) => {
      return value ? 'Yes' : 'No';
    },
    width: 90,
  },
  {
    field: 'created',
    headerName: 'Booked Date',
    valueGetter: (value: AppointmentSnapshot['created']) => convertUtcStringToLocalDate(value),
    valueFormatter: (value: Date) => format(value, 'MM/dd/yyyy | h:mmaaa'),
    renderCell: (params: GridRenderCellParams<AppointmentSnapshot, Date, string>) => (
      <NoWrapText text={params.formattedValue!} />
    ),
    width: 110,
  },
  {
    field: 'bookingChannel',
    headerName: 'Scheduled By',
    valueFormatter: (value: AppointmentSnapshot['bookingChannel']) =>
      ({
        staff: 'Staff',
        pss: 'Patient',
        import: 'Import',
      })[value],
    width: 120,
  },
  {
    field: 'leadTime',
    headerName: 'Lead Time',
    valueFormatter: (value: AppointmentSnapshot['leadTime']) => formatDurationFromSeconds(value),
    renderCell: (params: GridRenderCellParams<AppointmentSnapshot, number, string>) => (
      <NoWrapText text={params.formattedValue!} />
    ),
    width: 100,
  },
  {
    field: 'confirmedDate',
    headerName: 'Confirmation Status',
    valueGetter: (value: AppointmentSnapshot['confirmedDate']) =>
      value ? convertUtcStringToLocalDate(value) : null,
    valueFormatter: (value: Date | null) => (value ? format(value, 'MM/dd/yyyy | h:mmaaa') : null),
    renderCell: (params: GridRenderCellParams<AppointmentSnapshot, Date | null, string | null>) => (
      <>
        <Chip
          label={params.value ? 'Confirmed' : 'Unconfirmed'}
          color={params.value ? 'success' : 'warning'}
          size="small"
        />
        {params.formattedValue ? (
          <Typography component="span" variant="caption" display="block" mt={1}>
            {params.formattedValue}
          </Typography>
        ) : null}
      </>
    ),
    width: 160,
  },
];

const defaultData: AppointmentSnapshot[] = [];
const pageSizeOptions = [10, 25, 50, 100, 250, 500, 1_000, 5_000, 10_000];
const getRowHeight = () => 'auto' as const;

export function AppointmentsDrilldown() {
  const data = useLoaderData<Awaited<ReturnType<typeof loader>>>();

  const [, setSearchParams] = useSearchParams();

  const columnVisibility = useColumnVisibility({
    storageKey: 'appointments-drilldown-column-visibility',
  });

  const query = useQuery(
    appointmentSnapshot({
      page: data.page,
      pageSize: data.pageSize,
      sortBy: data.sortBy,
      // Add an asterisk if needed to trigger wildcard search in the API
      searchText: data.searchText ? `${data.searchText}*` : undefined,
      minStartDate: formatInTimeZone(startOfDay(data.from), 'UTC', FORMATS.MYSQL_DATETIME),
      maxStartDate: formatInTimeZone(endOfDay(data.to), 'UTC', FORMATS.MYSQL_DATETIME),
      providerId: data.providerIds,
      appointmentTypeId: data.appointmentTypeIds,
      addressId: data.addressIds,
      bookingChannel: data.bookingChannelIds,
      appointmentTypeType: data.baseAppointmentTypes,
    })
  );

  const [rowCount, setRowCount] = React.useState(0);

  if (query.data !== undefined && query.data.totalItems !== rowCount) {
    setRowCount(query.data.totalItems);
  }

  const titleId = React.useId();

  const close = useClose({ drilldownType: 'appointments' });

  return (
    <>
      <Dialog.Root
        open
        onClose={(reason) =>
          close.handler({
            force: reason === 'error',
            doNotShowFiltersDisclaimerAgain: false,
          })
        }
        fullWidth
        fullHeight
        maxWidth="xl"
        aria-labelledby={titleId}
      >
        <Dialog.Header>
          <Dialog.Title id={titleId}>Appointments Drilldown</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content>
          <DataGridPro
            rows={query.data?.data ?? defaultData}
            rowCount={rowCount}
            getRowHeight={getRowHeight}
            columns={columns}
            columnVisibilityModel={columnVisibility.model}
            onColumnVisibilityModelChange={columnVisibility.onChange}
            initialState={data.dataGridInitialState}
            loading={query.isLoading}
            showToolbar
            slots={{
              toolbar: Toolbar,
              filterPanel: AppointmentsFiltersPanel,
            }}
            checkboxSelection
            disableRowSelectionOnClick
            pagination
            paginationMode="server"
            onPaginationModelChange={(model) => {
              setSearchParams((searchParams) => {
                searchParams.set('page', String(model.page + 1));
                searchParams.set('pageSize', String(model.pageSize));
                return searchParams;
              });
            }}
            pageSizeOptions={pageSizeOptions}
            sortingMode="server"
            onSortModelChange={(model) => {
              const filteredItems = model.filter((model) => typeof model.sort === 'string');

              if (filteredItems.length === 0) {
                setSearchParams((searchParams) => {
                  searchParams.delete('sortBy');
                  return searchParams;
                });
                return;
              }

              setSearchParams((searchParams) => {
                searchParams.set(
                  'sortBy',
                  filteredItems
                    // Safe to use `!` as long as the filter criteria matches
                    .map((item) => `${item.field} ${item.sort!.toUpperCase()}`)
                    .join(',')
                );
                return searchParams;
              });
            }}
            filterMode="server"
            onFilterModelChange={(model) => {
              // The values come trimmed
              const searchText = model.quickFilterValues?.join(' ') ?? '';

              setSearchParams((searchParams) => {
                if (searchText.length < SEARCH_MIN_CHARS) {
                  searchParams.delete('searchText');
                } else {
                  searchParams.set('searchText', searchText);
                }
                return searchParams;
              });
            }}
            sx={{
              /**
               * When the 'auto' row height is used, the rows take the minimum
               * height of the content. The 10px value mimics the default
               * right/left padding of the cells.
               *
               * @see https://mui.com/x/react-data-grid/row-height/#dynamic-row-height
               */
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                py: '10px',
              },
            }}
          />
        </Dialog.Content>
      </Dialog.Root>

      {close.showFiltersDisclaimer && (
        <FiltersDisclaimerDialog
          onClose={close.dismissFiltersDisclaimer}
          onOk={(doNotShowAgain) =>
            close.handler({
              force: true,
              doNotShowFiltersDisclaimerAgain: doNotShowAgain,
            })
          }
        />
      )}
    </>
  );
}
